import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/realApi';

/**
 * Custom hook for managing learning agent chat state and operations.
 * Handles message history, sending messages to agent, mode management, and learning stats.
 */
export const useAgentChat = () => {
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState(null);
  const [learningStats, setLearningStats] = useState(null);
  const [currentMode, setCurrentMode] = useState('adaptive');
  const [activeWords, setActiveWords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);

  const messagesEndRef = useRef(null);

  /**
   * Load agent settings and initial messages
   */
  const loadInitialData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Load agent settings and chat messages in parallel
      const [agentSettingsResponse, messagesResponse] = await Promise.all([
        api.getAgentSettings(),
        api.getChatMessages({ limit: 50 }) // Reuse chat messages
      ]);

      setSettings(agentSettingsResponse.agentSettings);
      setLearningStats(agentSettingsResponse.learningStats);
      setCurrentMode(agentSettingsResponse.agentSettings?.preferredMode || 'adaptive');
      setMessages(messagesResponse.messages || []);
      setLastEvaluatedKey(messagesResponse.lastEvaluatedKey || null);
      setHasMore(!!messagesResponse.lastEvaluatedKey);
    } catch (err) {
      console.error('Error loading agent data:', err);
      setError(err.message || 'Failed to load agent data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Load more messages (for infinite scroll)
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.getChatMessages({
        limit: 50,
        lastEvaluatedKey: lastEvaluatedKey
      });

      setMessages(prev => [...(response.messages || []), ...prev]);
      setLastEvaluatedKey(response.lastEvaluatedKey || null);
      setHasMore(!!response.lastEvaluatedKey);
    } catch (err) {
      console.error('Error loading more messages:', err);
      setError(err.message || 'Failed to load more messages');
    } finally {
      setIsLoading(false);
    }
  }, [hasMore, isLoading, lastEvaluatedKey]);

  /**
   * Send a message to the learning agent
   * @param {string} message - The message to send
   * @param {string} forcedMode - Optional mode override (practice, quiz, free_chat)
   */
  const sendMessage = useCallback(async (message, forcedMode = null) => {
    if (!message || !message.trim()) {
      return;
    }

    setIsSending(true);
    setError(null);

    // Add user message immediately (optimistic update)
    const tempUserMessage = {
      messageId: `temp-${Date.now()}`,
      role: 'user',
      content: message.trim(),
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMessage]);

    try {
      const response = await api.sendAgentMessage(message, forcedMode);

      // Replace temp user message with real one (if returned) and add assistant message
      setMessages(prev => {
        // Remove the temp message
        const filtered = prev.filter(msg => msg.messageId !== tempUserMessage.messageId);
        // Add the real user message and assistant message
        const newMessages = [...filtered];
        if (response.userMessage) {
          newMessages.push(response.userMessage);
        }
        if (response.assistantMessage) {
          newMessages.push(response.assistantMessage);
        }
        return newMessages;
      });

      // Update state from response
      if (response.mode) {
        setCurrentMode(response.mode);
      }
      if (response.activeWords) {
        setActiveWords(response.activeWords);
      }
      if (response.learningStats) {
        setLearningStats(response.learningStats);
      }
    } catch (err) {
      console.error('Error sending agent message:', err);
      setError(err.response?.data?.error || err.message || 'Failed to send message');
      // Remove the temp message on error
      setMessages(prev => prev.filter(msg => msg.messageId !== tempUserMessage.messageId));
    } finally {
      setIsSending(false);
    }
  }, []);

  /**
   * Send message with forced mode
   */
  const sendPracticeMessage = useCallback((message) => {
    return sendMessage(message, 'practice');
  }, [sendMessage]);

  const sendQuizMessage = useCallback((message) => {
    return sendMessage(message, 'quiz');
  }, [sendMessage]);

  const sendFreeChatMessage = useCallback((message) => {
    return sendMessage(message, 'free_chat');
  }, [sendMessage]);

  /**
   * Update agent settings
   */
  const updateSettings = useCallback(async (newSettings) => {
    try {
      const response = await api.updateAgentSettings(newSettings);
      setSettings(response.agentSettings);
      if (newSettings.preferredMode) {
        setCurrentMode(newSettings.preferredMode);
      }
      return response;
    } catch (err) {
      console.error('Error updating agent settings:', err);
      setError(err.message || 'Failed to update settings');
      throw err;
    }
  }, []);

  /**
   * Set preferred mode
   */
  const setMode = useCallback(async (mode) => {
    setCurrentMode(mode);
    try {
      await api.updateAgentSettings({ preferredMode: mode });
    } catch (err) {
      console.error('Error setting mode:', err);
    }
  }, []);

  /**
   * Clear conversation
   */
  const clearConversation = useCallback(async () => {
    try {
      await api.clearChatMessages();
      setMessages([]);
      setLastEvaluatedKey(null);
      setHasMore(false);
      setActiveWords([]);
    } catch (err) {
      console.error('Error clearing conversation:', err);
      setError(err.message || 'Failed to clear conversation');
    }
  }, []);

  /**
   * Refresh learning stats
   */
  const refreshStats = useCallback(async () => {
    try {
      const response = await api.getLearningStats();
      setLearningStats(response.learningStats);
    } catch (err) {
      console.error('Error refreshing stats:', err);
    }
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load initial data on mount
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  return {
    // State
    messages,
    settings,
    learningStats,
    currentMode,
    activeWords,
    isLoading,
    isSending,
    error,
    hasMore,
    messagesEndRef,

    // Actions
    sendMessage,
    sendPracticeMessage,
    sendQuizMessage,
    sendFreeChatMessage,
    loadMore,
    loadInitialData,
    updateSettings,
    setMode,
    clearConversation,
    refreshStats,
    clearError
  };
};

export default useAgentChat;
